import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationPanel = ({ isOpen, onClose, notifications = [] }) => {
  const panelRef = useRef(null);

  const defaultNotifications = [
    {
      id: 1,
      type: 'urgent',
      title: 'New Support Request',
      message: 'Customer #4521 reported equipment failure',
      timestamp: '2 minutes ago',
      icon: 'AlertCircle',
      actionLabel: 'View Request',
      actionPath: '/support-request-management'
    },
    {
      id: 2,
      type: 'assignment',
      title: 'Technician Assignment',
      message: 'Tech #203 assigned to Job #8745',
      timestamp: '15 minutes ago',
      icon: 'UserCog',
      actionLabel: 'View Assignment',
      actionPath: '/technician-assignment'
    },
    {
      id: 3,
      type: 'schedule',
      title: 'Schedule Update',
      message: 'Job #8732 rescheduled to tomorrow 9:00 AM',
      timestamp: '1 hour ago',
      icon: 'Calendar',
      actionLabel: 'View Schedule',
      actionPath: '/ticket-management'
    }
  ];

  const displayNotifications = notifications?.length > 0 ? notifications : defaultNotifications;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef?.current && !panelRef?.current?.contains(event?.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const getNotificationColor = (type) => {
    switch (type) {
      case 'urgent':
        return 'text-error';
      case 'assignment':
        return 'text-primary';
      case 'schedule':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification?.actionPath) {
      window.location.href = notification?.actionPath;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-background z-[1200] lg:hidden" ref={panelRef}>
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-xl font-heading font-semibold text-foreground">Notifications</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-smooth"
            >
              <Icon name="X" size={24} color="var(--color-foreground)" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {displayNotifications?.map((notification) => (
              <div
                key={notification?.id}
                className="bg-card rounded-lg p-4 border border-border shadow-elevation-1 cursor-pointer hover:shadow-elevation-2 transition-smooth"
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1 ${getNotificationColor(notification?.type)}`}>
                    <Icon name={notification?.icon} size={20} color="currentColor" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground mb-1">{notification?.title}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{notification?.message}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-caption text-muted-foreground">
                        {notification?.timestamp}
                      </span>
                      {notification?.actionLabel && (
                        <span className="text-xs font-medium text-primary">
                          {notification?.actionLabel} →
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-border">
            <Button variant="outline" fullWidth onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
      <div
        ref={panelRef}
        className="hidden lg:block fixed top-16 right-4 w-96 bg-card rounded-lg shadow-elevation-4 z-[1200] max-h-[calc(100vh-5rem)] flex flex-col"
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-foreground">Notifications</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {displayNotifications?.map((notification) => (
            <div
              key={notification?.id}
              className="bg-muted/50 rounded-lg p-3 cursor-pointer hover:bg-muted transition-smooth"
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-1 ${getNotificationColor(notification?.type)}`}>
                  <Icon name={notification?.icon} size={18} color="currentColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm text-foreground mb-1">{notification?.title}</h3>
                  <p className="text-xs text-muted-foreground mb-2">{notification?.message}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-caption text-muted-foreground">
                      {notification?.timestamp}
                    </span>
                    {notification?.actionLabel && (
                      <span className="text-xs font-medium text-primary">
                        {notification?.actionLabel} →
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-border">
          <Button variant="ghost" fullWidth onClick={onClose}>
            Mark All as Read
          </Button>
        </div>
      </div>
    </>
  );
};

export default NotificationPanel;